import { Alert, Button, Checkbox, Form, Input, InputNumber, Modal, Radio, Select, Switch, Table, TreeSelect, Upload } from 'antd'
import { useEffect, useRef, useState, useTransition } from 'react'
import { useHistory } from 'react-router'
import publisherApi from 'src/component/app/publisher/PublisherApi'
import { AccountBean, DspBean, LanguageBean, PublisherBean, TimeZoneBean, CountryBean } from '../../../api/api'
import { formDataLoader, FormDataType } from '../../../util/FormDataLoader'
import { RevenueCalculationMethod, SellerType, getDefaultIabCategoryIds, getIabCategoriesAsTree, resolveIabDefaultsChange, stripSelectDefaultsValue, truncateErrorMessage } from '../../../util/Util'
import { CheckCircleOutlined, CloseOutlined, EyeOutlined, PlusOutlined, StopOutlined, UploadOutlined, ArrowLeftOutlined } from '@ant-design/icons';
import { StorageKey, storageWriterFactory } from '../../../util/Storage'
import PublisherTabs, { PublisherTabItem } from './PublisherTabs'
import { BasicDetails, FilteringOptions, PaymentDetails } from './PublisherTabSections'
import PublisherDomainListCard from './PublisherDomainListCard'

const FormItem = Form.Item
const Option = Select.Option

const formInfoItems: FormDataType[] = [
    FormDataType.DspList,
    FormDataType.IabCategories,
    FormDataType.Languages,
    FormDataType.TimeZones,
    FormDataType.Publishers,
    FormDataType.Countries
]

const WinNotificationEnum = DspBean.WinNotificationEnum

type PublisherCreateTab = 'basic' | 'filtering' | 'payment'
const publisherTabs: PublisherTabItem[] = [
    { key: 'basic', label: 'Basic Details' },
    { key: 'filtering', label: 'Filtering Options' },
    { key: 'payment', label: 'Payment Details' },
]

type PaymentCustomField = {
    id: string
    label: string
    placeholder?: string
}

const ADFI_REQUIRED_FIELD_NAMES = [
    'adfiFeePercent',
    'serviceFeePercent',
    'publisherLegalName',
    'publisherRegisteredAddress',
    'publisherContactEmail',
    'currencyPreference',
    'vatRate',
    'bankAccountName',
    'bankAccountType',
    'bankAccountNumber',
    'sortCode',
    'iban',
    'swiftBicCode',
    'routingNumber',
    'paymentTermsDays',
] as const

const PAYMENT_TAB_FIELDS = new Set([
    ...ADFI_REQUIRED_FIELD_NAMES,
    'vatRegistrationNumber',
    'customPaymentFields',
])

const PublisherCreate = () => {
    const [error, setError] = useState(undefined)
    const [validation, setValidation] = useState(undefined);
    const [useParentSellerAccountId, setuseParentSellerAccountId] = useState(false);
    const [parentPublisherId, setParentPublisherId] = useState(undefined);
    const [passThrough, setpassThrough] = useState(false);
    const [includeDomains, setIncludeDomains] = useState(false);
    const [form] = Form.useForm()
    const [saveMode, setSaveMode] = useState<'SAVE' | 'DRAFT'>('SAVE');
    const saveModeRef = useRef<'SAVE' | 'DRAFT'>('SAVE')
    const iabPrevRef = useRef<number[]>([])
    const storageWriter = storageWriterFactory('publisher');
    const history = useHistory()

    const [allClassification, setAllClassification] = useState(false)
    const [acceptAllMobileAppIds, setAcceptAllMobileAppIds] = useState(false)
    const [domainMobileError, setDomainMobileError] = useState('')

    const [activeTab, setActiveTab] = useState<PublisherCreateTab>('basic')
    const [adfiEligible, setAdfiEligible] = useState(false)
    const [isCustomFieldModalOpen, setIsCustomFieldModalOpen] = useState(false)
    const [customPaymentFields, setCustomPaymentFields] = useState<PaymentCustomField[]>([])
    const [customFieldForm] = Form.useForm()

    // ─── Domains state ──────────────────────────────────────────────────────
    const [domains, setDomains] = useState<string[]>([])
    const [domainsCollapsed, setDomainsCollapsed] = useState(false)

    // ─── Mobile App IDs state ───────────────────────────────────────────────
    const [mobileAppIds, setMobileAppIds] = useState<string[]>([])
    const [mobileAppsCollapsed, setMobileAppsCollapsed] = useState(false)

    const [info, setInfo] = useState({
        dspList: [],
        iabCategories: [],
        languages: [],
        timezones: [],
        publishers: [],
        countries: [],
    })

    const navigateToLastPage = async () => {
        try {
            const response = await publisherApi.list(1, 1);
            const total = response.pagination?.total || 1;
            const pageSize = 10;
            const lastPage = Math.ceil(total / pageSize);
            storageWriter.write(StorageKey.SearchParams, { page: lastPage });
            history.push('/publishers', { fromPublisherList: true });
        } catch {
            history.push('/publishers', { fromPublisherList: true });
        }
    };

    const validateDomainsOrMobileApps = (): boolean => {
        if (allClassification || acceptAllMobileAppIds) {
            setDomainMobileError('')
            return true
        }
        if (domains.length === 0 && mobileAppIds.length === 0) {
            setDomainMobileError('Either Domains or Mobile App IDs is required')
            setActiveTab('filtering')
            return false
        }
        setDomainMobileError('')
        return true
    }

    const handleSubmit = () => {
        const mode = saveModeRef.current
        if (mode === 'DRAFT') {
            const fieldsToValidate = adfiEligible ? ['name', ...adfiRequiredFieldNames] : ['name']
            form
                .validateFields(fieldsToValidate)
                .then(() => {
                    const allValues = form.getFieldsValue(true);
                    createPublisher(allValues, true);
                })
                .catch((err) => {
                    const firstField = err?.errorFields?.[0]?.name
                    const firstKey = Array.isArray(firstField) ? String(firstField[0]) : String(firstField)
                    if (new Set(adfiRequiredFieldNames).has(firstKey)) {
                        setActiveTab('payment')
                    } else {
                        setActiveTab('basic')
                    }
                    setTimeout(() => form.scrollToField(firstField, { behavior: 'smooth', block: 'center' }), 100)
                });
            return;
        }

        form
            .validateFields()
            .then(() => {
                if (!validateDomainsOrMobileApps()) return;
                const allValues = form.getFieldsValue(true);
                createPublisher(allValues, false);
            })
            .catch((err) => {
                const firstField = err?.errorFields?.[0]?.name
                const firstKey = Array.isArray(firstField) ? String(firstField[0]) : String(firstField)
                if (firstKey === 'countries' || firstKey === 'iabCategoriesIds' || firstKey === 'dspBlackListIds') {
                    setActiveTab('filtering')
                    setTimeout(() => form.scrollToField(firstField, { behavior: 'smooth', block: 'center' }), 100)
                    return
                }
                if (PAYMENT_TAB_FIELDS.has(firstKey)) {
                    if (!validateDomainsOrMobileApps()) return
                    setActiveTab('payment')
                } else {
                    setActiveTab('basic')
                }
                setTimeout(() => form.scrollToField(firstField, { behavior: 'smooth', block: 'center' }), 100)
            });
    };

    const createPublisher = (values: any, isDraft: boolean) => {
        const domainList: string[] = allClassification ? [] : domains
        const mobileAppIdList: string[] = acceptAllMobileAppIds ? [] : mobileAppIds
        const countries = values?.countries?.map((countryId: number) => ({ id: countryId })) || [];
        delete values.blackListDomainsField;

        const isAdFiEligible =
          values?.adfiEligible === undefined ? false : Boolean(values?.adfiEligible)

        const paymentDetails = {
            legalName: values?.publisherLegalName,
            registeredAddress: values?.publisherRegisteredAddress,
            contactEmail: values?.publisherContactEmail,
            currencyPreference: values?.currencyPreference,
            vatRegistrationNumber: values?.vatRegistrationNumber,
            vatRate: values?.vatRate,
            bankAccountName: values?.bankAccountName,
            bankAccountNumber: values?.bankAccountNumber,
            bankAccountType: values?.bankAccountType,
            sortCode: values?.sortCode,
            iban: values?.iban,
            routingNumber: values?.routingNumber,
            swiftBicCode: values?.swiftBicCode,
            paymentTermsDays: values?.paymentTermsDays,
            isAdFiEligible
        }

        const publisherPayload = { ...values }
        publisherPayload.adFiEnabled = Boolean(values?.adfiEligible)
        publisherPayload.adFiFee = values?.adfiFeePercent
        publisherPayload.serviceFee = values?.serviceFeePercent
        delete publisherPayload.adfiFeePercent
        delete publisherPayload.serviceFeePercent
        delete publisherPayload.adfiEligible
        delete publisherPayload.adfiEnabled
        delete publisherPayload.publisherLegalName
        delete publisherPayload.publisherRegisteredAddress
        delete publisherPayload.publisherContactEmail
        delete publisherPayload.currencyPreference
        delete publisherPayload.vatRate
        delete publisherPayload.vatRegistrationNumber
        delete publisherPayload.bankAccountName
        delete publisherPayload.bankAccountType
        delete publisherPayload.bankAccountNumber
        delete publisherPayload.sortCode
        delete publisherPayload.iban
        delete publisherPayload.swiftBicCode
        delete publisherPayload.routingNumber
        delete publisherPayload.paymentTermsDays
        delete publisherPayload.customPaymentFields

        publisherApi
            .create({
                ...publisherPayload,
                iabCategoriesIds: stripSelectDefaultsValue(values.iabCategoriesIds),
                domains: domainList,
                acceptAllDomains: allClassification,
                mobileAppIds: mobileAppIdList,
                acceptAllMobileAppIds,
                countries,
                validation: validation || false,
                useParentSellerAccountId,
                passThrough,
                includeDomains,
                ...(isDraft && { status: 'DRAFT' }),
            })
            .then(async (res) => {
                if (isDraft) {
                    const publisherId = res?.data?.id
                    // Persist payment details only when AdFI is eligible (toggle enabled).
                    // When AdFI is disabled, the payment-details endpoint must not be hit.
                    if (publisherId !== undefined && isAdFiEligible) {
                        await publisherApi.updatePaymentDetails(publisherId, paymentDetails)
                    }
                    Modal.info({
                        title: 'Publisher saved as Draft',
                        content: 'This publisher is in Draft status. Activate it to enable bidding and generate API credentials.',
                        okText: 'OK',
                        onOk: () => navigateToLastPage(),
                    });
                } else {
                    const publisherId = res?.data?.id
                    if (publisherId !== undefined) {
                        activatePublisher(publisherId, paymentDetails)
                    } else {
                        Modal.error({
                            title: 'Activation Failed',
                            content: 'Publisher was created but no ID was returned. Please refresh and try activating manually.',
                        })
                    }
                }
            })
            .catch(onRejected);
    };

    const activatePublisher = async (publisherId: number, paymentDetails?: any) => {
        try {
            // Persist payment details only when AdFI is eligible (toggle enabled).
            // When AdFI is disabled, the payment-details endpoint must not be hit.
            if (paymentDetails?.isAdFiEligible) {
                await publisherApi.updatePaymentDetails(publisherId, paymentDetails)
            }
            const response = await publisherApi.activate(publisherId)
            if (response?.data?.authDataBean) {
                userCredentials(response.data.authDataBean)
            } else {
                navigateToLastPage()
            }
        } catch (err: any) {
            Modal.error({
                title: 'Activation Failed',
                content: err?.error || 'Unable to activate publisher',
            })
        }
    };

    const userCredentials = (authDataBean) => {
        Modal.info({
            title: 'New account created',
            content: <div>
                <br />
                <div>Please save account credentials</div>
                <br />
                <div>Username: {authDataBean.username}</div>
                <div>Password: {authDataBean.password}</div>
            </div>,
            okText: 'OK',
            okCancel: false,
            onOk: () => navigateToLastPage(),
        });
    };

    const onRejected = err => {
        setError(truncateErrorMessage(err?.error))
    }

    const loadData = async () => {
        try {
            let formInfo = await formDataLoader(...formInfoItems)
            setInfo(formInfo as any)
            let iabs = getDefaultIabCategoryIds(formInfo.iabCategories as any);
            let dsps = formInfo?.dspList?.map((el: DspBean) => el?.id)
            form.setFieldsValue({
                ['iabCategoriesIds']: iabs,
                ['dspBlackListIds']: dsps,
            })
        } catch (e) {
            onRejected(e)
        }
    }

    useEffect(() => {
        loadData()
    }, [])

    const handleSelectAll = (value: any) => {
        if (value && value.length && value.includes('__all')) {
            if (value.length === info.dspList.length + 1) {
                return []
            }
            return [...(info.dspList.map((el: DspBean) => el.id))]
        }
        return value
    }

    const onChangeIab = (value: number[]) => {
        const next = resolveIabDefaultsChange(value || [], iabPrevRef.current, info.iabCategories as any)
        iabPrevRef.current = next
        form.setFieldsValue({ ['iabCategoriesIds']: next })
        return next
    }

    const handleValidation = (value) => { setValidation(value.target.checked) };
    const handleuseParentSellerAccountId = (value) => { setuseParentSellerAccountId(value.target.checked) };
    const handleParentPublisherId = (event) => { setParentPublisherId(event) }
    const handlepassThrough = (value) => { setpassThrough(value.target.checked) };
    const handleIncludeDomains = (value) => { setIncludeDomains(value.target.checked) };

    const adfiRequiredFieldNames = ADFI_REQUIRED_FIELD_NAMES

    const getAdfiRequiredRule = (fieldLabel: string) =>
        adfiEligible ? [{ required: true, message: `Please input ${fieldLabel}` }] : []

    const handleAdfiEligibleChange = (checked: boolean) => {
        setAdfiEligible(checked)
    }

    const clearAdfiFieldErrors = () => {
        form.setFields(adfiRequiredFieldNames.map((name) => ({ name, errors: [] })))
    }

    const handleOpenCustomFieldModal = () => {
        customFieldForm.resetFields()
        setIsCustomFieldModalOpen(true)
    }

    const handleAddCustomField = () => {
        customFieldForm
            .validateFields()
            .then((values) => {
                const label = (values.fieldLabel || '').trim()
                const placeholder = (values.placeholder || '').trim()
                const fieldId = `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`
                setCustomPaymentFields((prev) => [...prev, { id: fieldId, label, placeholder }])
                setIsCustomFieldModalOpen(false)
                customFieldForm.resetFields()
            })
            .catch(() => { })
    }

    const handleRemoveCustomField = (fieldId: string) => {
        setCustomPaymentFields((prev) => prev.filter((field) => field.id !== fieldId))
        form.setFieldsValue({
            customPaymentFields: {
                ...(form.getFieldValue('customPaymentFields') || {}),
                [fieldId]: undefined,
            },
        })
    }

    useEffect(() => {
        if (!useParentSellerAccountId) {
            form.setFields([{ name: "parentPublisherId", errors: [] }]);
        }
    }, [useParentSellerAccountId]);

    useEffect(() => {
        if (!adfiEligible) {
            clearAdfiFieldErrors()
        }
    }, [adfiEligible])

    // ─── Domain handlers ────────────────────────────────────────────────────
    const handleAddDomain = (value: string) => setDomains(prev => [...prev, value])
    const handleUploadDomains = (file: File) => {
        const reader = new FileReader()
        reader.onloadend = () => {
            const lines = (reader.result as string).split('\n').map(l => l.trim()).filter(Boolean)
            setDomains(prev => {
                const existingLower = new Set(prev.map(d => d.toLowerCase()))
                return [...prev, ...lines.filter(d => !existingLower.has(d.toLowerCase()))]
            })
        }
        reader.readAsText(file)
    }
    const handleRemoveDomain = (index: number) =>
        setDomains(prev => prev.filter((_, i) => i !== index))
    const handleClearAllDomains = () => setDomains([])

    // ─── Mobile App ID handlers ─────────────────────────────────────────────
    const handleAddMobileAppId = (value: string) => setMobileAppIds(prev => [...prev, value])
    const handleUploadMobileAppIds = (file: File) => {
        const reader = new FileReader()
        reader.onloadend = () => {
            const lines = (reader.result as string).split('\n').map(l => l.trim()).filter(Boolean)
            setMobileAppIds(prev => {
                const existingLower = new Set(prev.map(d => d.toLowerCase()))
                return [...prev, ...lines.filter(d => !existingLower.has(d.toLowerCase()))]
            })
        }
        reader.readAsText(file)
    }
    const handleRemoveMobileAppId = (index: number) =>
        setMobileAppIds(prev => prev.filter((_, i) => i !== index))
    const handleClearAllMobileAppIds = () => setMobileAppIds([])

    const isBasicTab = activeTab === 'basic'
    const isFilteringTab = activeTab === 'filtering'
    const isPaymentTab = activeTab === 'payment'

    return (
        <div>
            {error && <div><Alert message={error} type="error" /><br /></div>}
            <div
                style={{
                    position: 'sticky',
                    top: 0,
                    zIndex: 10,
                    background: '#fff',
                    border: '1px solid #f0f0f0',
                    borderRadius: 8,
                    padding: 12,
                    marginBottom: 16,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    gap: 12,
                }}
            >
                <Button type="default" icon={<ArrowLeftOutlined />} onClick={() => history.push('/publishers', { fromPublisherList: true })}>
                    Go Back
                </Button>
                <PublisherTabs
                    tabs={publisherTabs}
                    activeTab={activeTab}
                    onTabChange={(value) => setActiveTab(value as PublisherCreateTab)}
                />
                <div style={{ display: 'flex', gap: 8 }}>
                    <Button
                        style={{ background: '#10b981b8', color: '#fff' }}
                        htmlType="button"
                        onClick={() => {
                            setSaveMode('DRAFT')
                            saveModeRef.current = 'DRAFT'
                            handleSubmit()
                        }}
                    >
                        Save as Draft
                    </Button>
                    <Button
                        type="primary"
                        htmlType="button"
                        onClick={() => {
                            setSaveMode('SAVE')
                            saveModeRef.current = 'SAVE'
                            handleSubmit()
                        }}
                    >
                        Activate
                    </Button>
                </div>
            </div>
            <Form
                style={{ maxWidth: '1000px' }}
                onFinish={handleSubmit}
                form={form}
                initialValues={{ adfiEligible: false }}
                labelCol={{ span: 7 }}
                wrapperCol={{ span: 17 }}
            >
                <BasicDetails active={isBasicTab}>
                    <FormItem
                        name="name"
                        rules={[
                            { required: true, message: 'Please input Publisher name' },
                            { max: 255, message: 'Name cannot be longer that 255 characters' }
                        ]}
                        label="Name"
                        tooltip={'Name of the publisher'}
                        validateTrigger={false}
                    >
                        <Input />
                    </FormItem>
                    <FormItem
                        name="sellerAccountId"
                        rules={[{ type: 'integer', min: 1, max: 9999, message: 'Please input a valid id from 1 to 9999' }]}
                        label="SellerAccountId"
                        tooltip={'SellerAccountId of the publisher'}
                    >
                        <InputNumber style={{ width: '100%' }} controls={false} disabled />
                    </FormItem>
                    <FormItem
                        name="language"
                        label="Language"
                        tooltip={'Publisher language'}
                        rules={[{ max: 255, message: 'Language cannot be longer that 255 characters' }]}
                        validateTrigger={false}
                    >
                        <Select optionFilterProp="title" showSearch allowClear placeholder="Select language">
                            {info.languages.map((item: LanguageBean) => (
                                <Option title={item.name} key={item.id} value={item.id as string}>{item.name}</Option>))}
                        </Select>
                    </FormItem>
                    <FormItem name="winNotification" label="Win notification" initialValue="Nurl">
                        <Select allowClear placeholder="Select win notification type">
                            {Object.values(WinNotificationEnum).map(win => <Option key={win} value={win}>{win}</Option>)}
                        </Select>
                    </FormItem>
                    <FormItem name="bundle" rules={[{ max: 255, message: 'Bundle cannot be longer that 255 characters' }]} label="Bundle" tooltip={'Publisher\'s App bundle'}>
                        <Input />
                    </FormItem>
                    <FormItem name="storeUrl" rules={[{ max: 255, message: 'Store URL cannot be longer that 255 characters' }]} label="Store URL" tooltip={'Publisher\'s App store URL'}>
                        <Input />
                    </FormItem>
                    <FormItem name="auctionMode" label="Auction Mode" rules={[{ required: true, message: 'Please select Auction type' }]} tooltip={'Auction type, where 1 = First Price, 2 = Second Price Plus. Exchange-specific auction types can be defined using values greater than 500'}>
                        <Select placeholder="Select Auction Mode">
                            {Object.keys(PublisherBean.AuctionModeEnum).map((mode) => <Option key={mode} value={mode}>{mode.replace(/([a-z])([A-Z])/g, '$1 $2')}</Option>)}
                        </Select>
                    </FormItem>
                    <FormItem name="houseBidderMode" label="House Bidder Mode" rules={[{ required: true, message: 'Please select House Bidder Mode' }]} tooltip={'House Bidder Mode'}>
                        <Select placeholder="Select House Bidder Mode">
                            {Object.keys(PublisherBean.HouseBidderModeEnum).map((mode) => <Option key={mode} value={mode}>{mode.replace(/([a-z])([A-Z])/g, '$1 $2')}</Option>)}
                        </Select>
                    </FormItem>
                    <FormItem required label="Alkimi Exchange Fee" tooltip="Publisher Fee">
                        <FormItem name="fee" initialValue={3} rules={[{ required: true, message: 'Fee is required' }]} className="ant-item ant-item-first">
                            <InputNumber step={0.01} min={0} max={99} />
                        </FormItem>
                        <FormItem className="ant-item ant-item-second">%</FormItem>
                    </FormItem>
                    <FormItem name="sellerDomain" label="Seller Domain" rules={[{ required: true, message: 'Please input Seller Domain' }]} tooltip={'Seller domains (domain1)'}>
                        <Input />
                    </FormItem>
                    <FormItem name="sellerType" label="Seller Type" rules={[{ required: true, message: 'Please select Seller type' }]}>
                        <Select placeholder="Select Seller type">
                            {SellerType.map(element => <Option key={element.value} value={element.value}>{element.label}</Option>)}
                        </Select>
                    </FormItem>
                    <FormItem name="revenueCalculationMethod" label="Revenue Calculation Method" rules={[{ required: true, message: 'Please select Revenue Calculation Method' }]}>
                        <Select placeholder="Select Revenue Calculation Method">
                            {RevenueCalculationMethod.map(element => <Option key={element.value} value={element.value}>{element.label}</Option>)}
                        </Select>
                    </FormItem>
                    <FormItem name="passThrough" label="Pass Through" valuePropName="checked">
                        <Checkbox onChange={handlepassThrough} />
                    </FormItem>
                    <FormItem name="useParentSellerAccountId" label="Use Parent Seller Id" valuePropName="checked">
                        <Checkbox onChange={handleuseParentSellerAccountId} />
                    </FormItem>
                    <FormItem
                        name="parentPublisherId"
                        label="Parent Publisher"
                        rules={useParentSellerAccountId ? [{ required: true, message: 'Please select parent publisher id' }] : []}
                        tooltip={'Select Parent Publisher Id'}
                    >
                        <Select showSearch allowClear onChange={handleParentPublisherId} placeholder="Select parent publisher id" filterOption={(input, option) => option?.children?.toLowerCase()?.includes(input?.toLowerCase())}>
                            {info?.publishers?.map((item: DspBean) => (
                                <Option key={item.id} value={item.id as number}>{item.name}</Option>
                            ))}
                        </Select>
                    </FormItem>
                    <FormItem name="validation" label="Validation" valuePropName="checked" tooltip={'If true publisher should send signed request only'}>
                        <Checkbox onChange={handleValidation} />
                    </FormItem>
                    <FormItem name="publicValidationKey" label="Public validation key" rules={[{ max: 255, message: 'Public validation key cannot be longer that 255 characters' }]} tooltip={'Public validation key need to validate publisher request'}>
                        <Input disabled={!validation} />
                    </FormItem>
                    <FormItem name="email" rules={[{ required: true, message: 'Please input Publisher email' }, { pattern: new RegExp('.+@.+\\..+'), message: 'Email is incorrect' }]} label="Email">
                        <Input />
                    </FormItem>
                    <FormItem name="timezoneId" rules={[{ required: true, message: 'Please select Time Zone', type: 'string' }]} label="Time Zone">
                        <Select showSearch placeholder="Select time zone">
                            {info.timezones.map((timezone: TimeZoneBean) => <Option key={timezone.id} value={timezone.id as string}>{timezone.id}</Option>)}
                        </Select>
                    </FormItem>
                    <FormItem name="test" label="Restrict Blockchain Transaction" valuePropName="checked" tooltip="If true publisher transactions will not be sent to Blockchain ledger">
                        <Checkbox />
                    </FormItem>
                    <FormItem required label="Validations limit" tooltip="Limit the number of validation report per month">
                        <FormItem name="athenaValidationsLimit" rules={[{ required: true, message: 'Limit is required' }]} className="ant-item ant-item-first">
                            <InputNumber min={0} defaultValue={0} />
                        </FormItem>
                    </FormItem>
                    <FormItem required label="Request/Response Id period limit" tooltip="Limit of period for Request/Response Id report in hours per month">
                        <FormItem name="athenaPeriodLimit" rules={[{ required: true, message: 'Limit is required' }]} className="ant-item ant-item-first">
                            <InputNumber min={0} defaultValue={0} />
                        </FormItem>
                    </FormItem>
                </BasicDetails>

                <FilteringOptions active={isFilteringTab}>
                    <FormItem name="maxQps" label="QPS" tooltip={'Maximum queries per second allowed'} rules={[{ pattern: /^\d*$/, message: 'Please input a valid number' }]}>
                        <Input />
                    </FormItem>
                    <FormItem
                        label="Allowed Domains"
                        tooltip={'Publisher allowed domains with classifications'}
                        validateStatus={domainMobileError ? 'error' : undefined}
                        help={domainMobileError || undefined}
                    >
                        <PublisherDomainListCard
                            title="Web Domains"
                            subtitle="Manage allowlist entries"
                            items={domains}
                            acceptAll={allClassification}
                            collapsed={domainsCollapsed}
                            onToggleCollapse={() => setDomainsCollapsed(v => !v)}
                            onAcceptAllChange={checked => {
                                setAllClassification(checked)
                                if (checked) setDomains([])
                                setDomainMobileError('')
                            }}
                            onAdd={handleAddDomain}
                            onUpload={handleUploadDomains}
                            onRemoveItem={handleRemoveDomain}
                            onClearAll={handleClearAllDomains}
                        />
                    </FormItem>
                    <FormItem name="includeDomains" label="Include Allowed Domains in sellers.JSON" valuePropName="checked">
                        <Checkbox checked={includeDomains} onChange={handleIncludeDomains} />
                    </FormItem>
                    <FormItem
                        label="Allowed Mobile App IDs"
                        tooltip={'Publisher allowed mobile app IDs with classifications'}
                        validateStatus={domainMobileError ? 'error' : undefined}
                        help={domainMobileError || undefined}
                    >
                        <PublisherDomainListCard
                            title="MobileApp ID"
                            subtitle="Manage allowlist entries"
                            items={mobileAppIds}
                            acceptAll={acceptAllMobileAppIds}
                            collapsed={mobileAppsCollapsed}
                            onToggleCollapse={() => setMobileAppsCollapsed(v => !v)}
                            onAcceptAllChange={checked => {
                                setAcceptAllMobileAppIds(checked)
                                if (checked) setMobileAppIds([])
                                setDomainMobileError('')
                            }}
                            onAdd={handleAddMobileAppId}
                            onUpload={handleUploadMobileAppIds}
                            onRemoveItem={handleRemoveMobileAppId}
                            onClearAll={handleClearAllMobileAppIds}
                        />
                    </FormItem>
                    <FormItem name="iabCategoriesIds" label="Publisher IAB Categories" tooltip={'IAB categories publisher wants to work with'}>
                        <TreeSelect
                            allowClear
                            treeCheckable
                            showCheckedStrategy="SHOW_ALL"
                            style={{ maxHeight: '400px', overflow: 'auto' }}
                            placement={'topRight'}
                            placeholder="Select IAB categories"
                            onChange={onChangeIab}
                            treeData={getIabCategoriesAsTree(info.iabCategories)}
                        />
                    </FormItem>
                    <FormItem name="dspBlackListIds" label="Dsp blacklist" getValueFromEvent={handleSelectAll} tooltip={'Dsps to do not get ads from'}>
                        <Select showSearch allowClear mode="multiple" placeholder="Select dsp">
                            {info.dspList.length > 0 && <Option value={'__all'}>{'Select all dsps'}</Option>}
                            {info.dspList.map((item: DspBean) => (
                                <Option key={item.id} value={item.id as number}>{item.name}</Option>
                            ))}
                        </Select>
                    </FormItem>
                    <FormItem name="countries" label="Geo Countries" tooltip="Geo Countries">
                        <Select mode="multiple" showSearch allowClear placeholder="Select Countries" optionFilterProp="children" filterOption={(input, option) => (option?.children as unknown as string)?.toLowerCase().indexOf(input.toLowerCase()) >= 0}>
                            {info.countries.map((item: CountryBean) => (
                                <Option key={item.id} value={item.id}>{item.name}</Option>
                            ))}
                        </Select>
                    </FormItem>
                </FilteringOptions>

                <PaymentDetails active={isPaymentTab}>
                    <FormItem name="adfiEligible" label="AdFI Eligible" valuePropName="checked">
                        <Switch onChange={handleAdfiEligibleChange} disabled={adfiEligible} />
                    </FormItem>
                    <FormItem name="adfiFeePercent" label="AdFI Fee %" rules={getAdfiRequiredRule('AdFI Fee %')}>
                        <InputNumber style={{ width: '100%' }} min={0} max={100} step={0.01} disabled={!adfiEligible} />
                    </FormItem>
                    <FormItem name="serviceFeePercent" label="Service Fee %" rules={getAdfiRequiredRule('Service Fee %')}>
                        <InputNumber style={{ width: '100%' }} min={0} max={100} step={0.01} disabled={!adfiEligible} />
                    </FormItem>
                    <FormItem name="publisherLegalName" label="Publisher Legal Name" rules={getAdfiRequiredRule('Publisher Legal Name')}>
                        <Input disabled={!adfiEligible} />
                    </FormItem>
                    <FormItem name="publisherRegisteredAddress" label="Publisher Registered Address" rules={getAdfiRequiredRule('Publisher Registered Address')}>
                        <Input disabled={!adfiEligible} />
                    </FormItem>
                    <FormItem
                        name="publisherContactEmail"
                        label="Publisher Contact Email"
                        rules={[
                            ...getAdfiRequiredRule('Publisher Contact Email'),
                            { pattern: new RegExp('^$|.+@.+\\..+'), message: 'Email is incorrect' },
                        ]}
                    >
                        <Input disabled={!adfiEligible} />
                    </FormItem>
                    <FormItem name="currencyPreference" label="Currency Preference" rules={getAdfiRequiredRule('Currency Preference')}>
                        <Input disabled={!adfiEligible} />
                    </FormItem>
                    <FormItem name="vatRate" label="VAT Rate" rules={getAdfiRequiredRule('VAT Rate')}>
                        <InputNumber style={{ width: '100%' }} min={0} max={100} step={0.01} disabled={!adfiEligible} />
                    </FormItem>
                    <FormItem name="vatRegistrationNumber" label="VAT Registration Number">
                        <Input disabled={!adfiEligible} />
                    </FormItem>
                    <FormItem name="bankAccountName" label="Bank Account Name" rules={getAdfiRequiredRule('Bank Account Name')}>
                        <Input disabled={!adfiEligible} />
                    </FormItem>
                    <FormItem name="bankAccountType" label="Bank Account Type" rules={adfiEligible ? [{ required: true, message: 'Please select Bank Account Type' }] : []}>
                        <Radio.Group disabled={!adfiEligible}>
                            <Radio value="CHECKING">Checking</Radio>
                            <Radio value="SAVINGS">Savings</Radio>
                        </Radio.Group>
                    </FormItem>
                    <FormItem name="bankAccountNumber" label="Bank Account Number" rules={getAdfiRequiredRule('Bank Account Number')}>
                        <Input disabled={!adfiEligible} />
                    </FormItem>
                    <FormItem name="sortCode" label="Sort Code" rules={getAdfiRequiredRule('Sort Code')}>
                        <Input disabled={!adfiEligible} />
                    </FormItem>
                    <FormItem name="iban" label="IBAN" rules={getAdfiRequiredRule('IBAN')}>
                        <Input disabled={!adfiEligible} />
                    </FormItem>
                    <FormItem name="swiftBicCode" label="Swift / BIC Code" rules={getAdfiRequiredRule('Swift / BIC Code')}>
                        <Input disabled={!adfiEligible} />
                    </FormItem>
                    <FormItem name="routingNumber" label="Routing Number" rules={getAdfiRequiredRule('Routing Number')}>
                        <Input disabled={!adfiEligible} />
                    </FormItem>
                    <FormItem name="paymentTermsDays" label="Payment Terms (Days)" rules={getAdfiRequiredRule('Payment Terms (Days)')}>
                        <InputNumber style={{ width: '100%' }} min={0} disabled={!adfiEligible} />
                    </FormItem>
                    {customPaymentFields.map((field) => (
                        <FormItem key={field.id} name={['customPaymentFields', field.id]} label={field.label}>
                            <Input
                                disabled={!adfiEligible}
                                placeholder={field.placeholder || undefined}
                                addonAfter={
                                    <Button type="link" danger style={{ padding: 0, height: 'auto' }} onClick={() => handleRemoveCustomField(field.id)}>
                                        Remove
                                    </Button>
                                }
                            />
                        </FormItem>
                    ))}
                </PaymentDetails>
            </Form>

            <Modal
                title="Add Custom Field"
                open={isCustomFieldModalOpen}
                onCancel={() => setIsCustomFieldModalOpen(false)}
                onOk={handleAddCustomField}
                okText="Add Field"
                destroyOnClose
            >
                <Form layout="vertical" form={customFieldForm}>
                    <FormItem name="fieldLabel" label="Field Label" rules={[{ required: true, message: 'Please input field label' }]}>
                        <Input placeholder="e.g Sort Code" />
                    </FormItem>
                    <FormItem name="placeholder" label="Placeholder / Hint (optional)">
                        <Input placeholder="e.g Enter your Sort Code" />
                    </FormItem>
                </Form>
            </Modal>
        </div>
    )
}

export default PublisherCreate
